import { type TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import { FOOTBALL_MARKET_NEW_SIGNINGS } from '@sorare/core/src/constants/__generated__/routes';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';

import { LeagueLaunch } from 'lib/newSeason';

import type { ShopAllCards_competition } from './__generated__/index.graphql';

type Competition = ShopAllCards_competition | undefined;
type Props = {
  league: LeagueLaunch<Competition, object | undefined>;
} & ComponentProps<typeof Button>;

export const ShopAllCards = ({ league, ...props }: Props) => {
  const { lifecycle } = useLifecycle();
  const rarity =
    lifecycle?.[LIFECYCLE.newFootballSeasonRarityFilter] || Rarity.limited;
  const leagueSlug = `${league.competition?.displayName || league.name}|${league.competition?.slug || league.slug}`;

  return (
    <Button
      {...props}
      to={`${generatePath(FOOTBALL_MARKET_NEW_SIGNINGS)}?${buildFilterQuery({
        [SEARCH_PARAMS.ACTIVE_LEAGUE]: leagueSlug,
        ...(rarity && { [SEARCH_PARAMS.RARITY]: rarity }),
      })}`}
    >
      <FormattedMessage id="newSeason.ShopCards2" defaultMessage="Shop cards" />
    </Button>
  );
};

ShopAllCards.fragments = {
  competition: gql`
    fragment ShopAllCards_competition on Competition {
      slug
      displayName
    }
  ` as TypedDocumentNode<ShopAllCards_competition>,
};
